import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Camera, Mic, Webcam, Smartphone, Aperture, Wifi, Signal, Lightbulb,
  Printer, Headphones, Plug, ListChecks,
  Youtube, Facebook, Twitch, RadioTower,
  Eye, Move, UserPlus, Disc3, CloudUpload, Route,
} from "lucide-react";
import { Lead, P, Section, Bullets, Steps, Step, Callout, UI, CardGrid, InfoCard } from "./ui";

/* ------------------------------------------------------------------ */
/* MEMBER GUIDES                                                       */
/* ------------------------------------------------------------------ */

const Welcome = () => (
  <div className="space-y-8">
    <Lead>
      The portal is your home for Friday Conversations — your membership, the podcast, events,
      and (for producers) the whole production studio. Here is everything you need to find your way.
    </Lead>
    <Section title="Signing in">
      <P>Open the app and sign in with your email and password, or with Google. Forgot your password?
        Click <UI>Forgot password</UI> on the sign-in screen and we'll email you a secure reset link.</P>
      <P>New members are guided through a short onboarding the first time, then land on the Dashboard.
        If you joined through a member's invitation or a QR code, you're approved instantly — no waiting.</P>
    </Section>
    <Section title="Install it like an app">
      <P>Friday Conversations is a Progressive Web App, so you can install it to your laptop dock or
        phone home screen for one-tap access and a full-screen, app-like feel.</P>
      <Bullets>
        <span>On the sidebar (or menu), tap <UI>Install app</UI> when it appears.</span>
        <span>On iPhone Safari: tap Share → <UI>Add to Home Screen</UI>.</span>
      </Bullets>
    </Section>
    <Section title="Finding your way around">
      <P>The left sidebar is grouped so you only see what is relevant to you:</P>
      <Bullets>
        <span><strong>Member</strong> — Dashboard, Podcast Hub, Member Directory, Shared Clips, Rewards, My Account. Everyone has these.</span>
        <span><strong>Producer</strong> — the Studio. Appears if you are a producer.</span>
        <span><strong>Leadership</strong> — Members, Voice Room, Analytics. Appears for the Core team.</span>
        <span><strong>Admin</strong> — Manage (events, media, storage, etc.). Appears for admins.</span>
      </Bullets>
      <Callout type="tip" title="On a phone?">
        Tap the menu icon at the top-left to open the same navigation in a slide-out panel.
      </Callout>
    </Section>
    <Section title="Light or dark">
      <P>Use the theme toggle in the top bar to switch between light and dark looks. Your choice is
        remembered on this device.</P>
    </Section>
    <Section title="Getting help">
      <P>You are reading it. This Help &amp; Guides hub covers the whole app. Producers should bookmark
        the <Link className="font-medium text-primary hover:underline" to="/portal/guides/producer-setup">Producer setup guide</Link> for traveling shows.</P>
    </Section>
  </div>
);

const MemberCard = () => (
  <div className="space-y-8">
    <Lead>Your member profile lives under <UI>My Account</UI>, which is split into three simple tabs.</Lead>
    <Section title="The three tabs">
      <Bullets>
        <span><strong>My Card</strong> — your premium digital member card (the one that flips). This is what people see when you share you.</span>
        <span><strong>My Business</strong> — your company details, what you do, and what referrals you are looking for.</span>
        <span><strong>Profile &amp; Privacy</strong> — your name, photo, contact details, and exactly what is visible to others.</span>
      </Bullets>
    </Section>
    <Section title="Edit your details">
      <Steps>
        <Step title="Open My Account">From the sidebar choose <UI>My Account</UI>.</Step>
        <Step title="Update each tab">Fill in your photo, title, business, and contact info. Changes save as you go.</Step>
        <Step title="Set your privacy">On <UI>Profile &amp; Privacy</UI>, choose what other members can see.</Step>
      </Steps>
    </Section>
    <Section title="Share your card">
      <P>Your digital card has its own public link and QR code, so you can share it at events even with
        people who are not members. Open <UI>My Card</UI> and use the share option to copy the link or show the QR.</P>
      <Callout type="info" title="You are in control">
        Only the fields you choose to make public appear on your shareable card.
      </Callout>
    </Section>
  </div>
);

const PodcastHubGuide = () => (
  <div className="space-y-8">
    <Lead>The Podcast Hub is where the community gathers around live recordings and replays.</Lead>
    <Section title="Join a live show">
      <Steps>
        <Step title="Open the Podcast Hub">Choose <UI>Podcast Hub</UI> from the sidebar.</Step>
        <Step title="Pick the live session">When a show is live, it appears at the top. Open it to watch.</Step>
        <Step title="Join the conversation">Use chat and reactions to take part in real time.</Step>
      </Steps>
    </Section>
    <Section title="Replays & catch-up">
      <P>Missed it live? Past sessions and their recordings stay in the Hub so you can watch any time.</P>
    </Section>
    <Section title="Want to be a guest?">
      <P>Every member is podcast-eligible — you'll see a <strong>podcast</strong> badge on your profile and member
        card. When you'd like to appear, request a spot from the Hub and leadership confirms your session.</P>
    </Section>
  </div>
);

const Clips = () => (
  <div className="space-y-8">
    <Lead>Clips are short, shareable highlights pulled from recordings — perfect for social media.</Lead>
    <Section title="Find clips">
      <P>Open <UI>Shared Clips</UI> from the sidebar to browse highlights from recent shows.</P>
    </Section>
    <Section title="Share a clip">
      <P>Each public clip has its own link and player. Copy the link and post it anywhere — the person
        you share it with does not need an account to watch.</P>
      <Callout type="tip" title="Producers">
        You can turn any clip into ready-to-post captions with the AI Social Post Generator. See the
        <Link className="ml-1 font-medium text-primary hover:underline" to="/portal/guides/social-posts">social posts guide</Link>.
      </Callout>
    </Section>
  </div>
);

const Rewards = () => (
  <div className="space-y-8">
    <Lead>Rewards recognise members who help the community grow.</Lead>
    <Section title="How it works">
      <Bullets>
        <span>Open <UI>Rewards</UI> from the sidebar to see your progress.</span>
        <span>Invite people to Friday Conversations using your referral link.</span>
        <span>Track who has joined and the rewards you have earned.</span>
      </Bullets>
    </Section>
    <Section title="Your invites skip the queue">
      <P>Anyone who joins through your referral link (or a QR code) becomes a <strong>full member
        instantly</strong> — no waiting for approval. They're signed straight in and ready to take part.
        People who find us on their own still go through a quick approval first.</P>
    </Section>
  </div>
);

/* ------------------------------------------------------------------ */
/* PRODUCER GUIDES                                                     */
/* ------------------------------------------------------------------ */

const StudioOverview = () => (
  <div className="space-y-8">
    <Lead>
      The Studio is one screen that runs the whole podcast: your Owl camera, remote guests, extra
      phone cameras, on-screen name plates, cloud recording and the live stream to members.
    </Lead>
    <Section title="Two places, two jobs">
      <CardGrid>
        <InfoCard icon={Disc3} title="Studio (Producer → Studio)">Create a show, open the Producer Console, run the episode. Everything live happens here.</InfoCard>
        <InfoCard icon={CloudUpload} title="Library & Tools">Recordings (with transcripts and AI insights), hosted videos, name badges, social posts.</InfoCard>
      </CardGrid>
    </Section>
    <Section title="The Producer Console at a glance">
      <Bullets>
        <span><strong>Program monitor</strong> — exactly what the audience sees and what gets recorded.</span>
        <span><strong>Layout</strong> — Solo, Split, Grid, Picture-in-picture, or Auto speaker (follows whoever is talking).</span>
        <span><strong>Sources</strong> — every camera and guest in the room. Click one (or press its number key 1–9) to put it on air; 0 shows the holding card.</span>
        <span><strong>Overlays</strong> — name plate (lower third), banner text, audience question card, logo, theme.</span>
        <span><strong>Audience questions</strong> — live Hub Q&amp;A; one click puts a question on screen.</span>
        <span><strong>Lobby</strong> — guests who opened the link and are waiting for you to admit them.</span>
        <span><strong>Phone &amp; wireless cameras</strong> — an always-visible QR code (tap for full-screen) that turns any phone into a camera.</span>
        <span><strong>Invite</strong> — the guest link (with QR code).</span>
        <span><strong>Your camera &amp; mic</strong> — switch USB devices instantly without leaving air.</span>
      </Bullets>
    </Section>
    <Section title="A typical Friday">
      <Steps>
        <Step title="Create the show">Producer → Studio → <UI>New show</UI>. Link it to the day's event so the title fills in.</Step>
        <Step title="Open the console">Your laptop camera (the Owl) joins the room automatically and appears under Sources.</Step>
        <Step title="Bring people in">Share the guest link with remote guests and admit them from the Lobby. Open the camera link on a phone for a second angle.</Step>
        <Step title="Record and go live">Once your tile is visible, press <UI>Record</UI> (and <UI>Go live</UI> if members should watch in the Hub). Wait for "Recording in progress".</Step>
        <Step title="Produce">Click sources to switch, tap the tag icon for a name plate, add a banner for the topic.</Step>
        <Step title="End the show">Press <UI>End show</UI>. The MP4 lands in the Library within a minute or two, ready to transcribe.</Step>
      </Steps>
    </Section>
    <Callout type="info" title="Why it's reliable now">
      The recording is made in the cloud by Cloudflare from the program output — not in your browser tab.
      A flaky Wi-Fi moment on stage no longer ruins the file.
    </Callout>
  </div>
);

const GoingLive = () => (
  <div className="space-y-8">
    <Lead>Record to the cloud, stream to members, or both — from two buttons in the Producer Console.</Lead>
    <Section title="Record">
      <Steps>
        <Step title="Wait for your tile">The <UI>Record</UI> button turns on once your camera has joined the room.</Step>
        <Step title="Press Record">A cloud recorder boots in about 20 seconds. The status reads "Recorder is starting up…" then "Recording in progress" with a red REC timer.</Step>
        <Step title="Stop when done">Press <UI>Stop recording</UI> (or <UI>End show</UI>). Cloudflare finalizes the MP4 and it appears in Library → Recordings automatically.</Step>
      </Steps>
      <Callout type="warn" title="Give it 20 seconds">Stopping before the recorder has finished starting saves nothing — the console tells you if that happens.</Callout>
    </Section>
    <Section title="Go live to members">
      <P>Press <UI>Go live</UI>. The same program output is streamed to a Cloudflare Stream channel and the show's
        Hub session flips to live, so members can watch, chat and ask questions in the Podcast Hub. Press
        <UI>End stream</UI> to stop; the Hub goes back to "off air".</P>
      <P>You can record without streaming (a private recording) or stream without recording — they're independent.</P>
    </Section>
    <Section title="Using OBS or a hardware encoder instead">
      <P>Under <UI>Recording &amp; stream</UI> in the console, expand <UI>OBS / hardware encoder details</UI> for the RTMPS server and key of the show's channel.</P>
    </Section>
  </div>
);

const GuestsCameras = () => (
  <div className="space-y-8">
    <Lead>Remote guests and extra cameras join with one link — no account, no app install.</Lead>
    <Section title="Guests">
      <Steps>
        <Step title="Send the guest link">Copy it from <UI>Invite</UI> in the console (or show the QR code on screen).</Step>
        <Step title="They enter their details">Name, title and organization — this is what their name plate will say.</Step>
        <Step title="Camera check in the lobby">They see their own camera while they wait. You get a notification.</Step>
        <Step title="Admit">Click <UI>Admit</UI> in the Lobby panel. They're in the room and appear under Sources.</Step>
        <Step title="Put them on air">Click their tile. Their screen shows a red <strong>ON AIR</strong> badge so they know they're live.</Step>
      </Steps>
    </Section>
    <Section title="Extra cameras (phones, a second laptop)">
      <P>Scan the QR code in the console's <UI>Phone &amp; wireless cameras</UI> card (tap it for a full-screen code
        people can scan from across the room). The phone picks a label (Wide shot, Host, Audience…), chooses back or
        front camera, and joins instantly with the microphone off (the room already has audio). It keeps the screen
        awake, reconnects by itself if Wi-Fi hiccups, and shows a red ON AIR frame when you switch to it.</P>
      <Bullets>
        <span><strong>Switch fast</strong> — click the tile or press its number key. The tile shows the incoming resolution; an amber "portrait" badge means the phone should be turned sideways.</span>
        <span><strong>Adjust the picture</strong> — the ⚙ button on any tile gives Mirror, Rotate 90°, and Fill/Fit (Fit keeps a portrait phone whole instead of cropping).</span>
        <span><strong>Rename or remove</strong> — also under ⚙. Renaming updates the phone's screen; removing disconnects it.</span>
      </Bullets>
      <Callout type="info" title="The Owl stays your main camera">
        Your laptop joins as the host. Pick the Owl (or any USB camera) in <UI>Your camera &amp; mic</UI>; switching devices is instant.
      </Callout>
    </Section>
    <Section title="Housekeeping">
      <Bullets>
        <span>Mute a noisy guest from their tile (mic icon).</span>
        <span>Edit anyone's name/title/org from their tile — the name plate updates live.</span>
        <span>Remove someone with the person-minus icon; they're taken off air automatically.</span>
      </Bullets>
    </Section>
  </div>
);

const RecordingsClips = () => (
  <div className="space-y-8">
    <Lead>Every recording flows through the same pipeline: capture → transcript → AI analysis → clips → social posts.</Lead>
    <Section title="Find your recordings">
      <P>Library &amp; Tools → <UI>Library</UI>. Every show recorded from the Studio lands here automatically as
        an MP4 a minute or two after you stop — along with anything you upload. See the <Link className="font-medium text-primary hover:underline" to="/portal/guides/going-live">Recording &amp; going live</Link> guide.</P>
    </Section>
    <Section title="Transcripts & AI analysis">
      <P>Recordings are <strong>transcribed automatically</strong> and analyzed by AI — suggested moments, topics, and
        pull-quotes that make great clips. Open a recording to read its transcript inline.</P>
      <Callout type="tip" title="Looking for all your transcripts?">
        The Studio’s dedicated <UI>Transcripts</UI> tab is a searchable library of every transcript — with speaker
        labels, copy and download. See the
        <Link className="ml-1 font-medium text-primary hover:underline" to="/portal/guides/transcripts">Transcripts guide</Link>.
      </Callout>
    </Section>
    <Section title="Make a clip">
      <Steps>
        <Step title="Open the clip editor">From a recording, choose to create clips.</Step>
        <Step title="Trim the moment">Set the start and end of the highlight and save it.</Step>
        <Step title="Share or repurpose">Share the public clip link, or send it to the Social generator for captions.</Step>
      </Steps>
    </Section>
  </div>
);

const SocialPosts = () => (
  <div className="space-y-8">
    <Lead>Turn a clip, recording, or video into polished, platform-specific posts — written by AI, ready
      to copy and paste. No auto-posting; you stay in control of what goes out.</Lead>
    <Section title="Generate posts">
      <Steps>
        <Step title="Open Social">Studio → <UI>Social</UI>.</Step>
        <Step title="Pick a source">Choose a clip, recording, or video from the picker.</Step>
        <Step title="Choose platforms & tone">Select any of LinkedIn, X/Twitter, Instagram, Facebook, and a tone (e.g. professional, casual, bold).</Step>
        <Step title="Generate">The AI writes a tailored post per platform with hashtags — and a link to the public clip when available.</Step>
      </Steps>
    </Section>
    <Section title="Edit, copy & reuse">
      <Bullets>
        <span>Tweak any post inline before you use it.</span>
        <span>Copy a post with one click and paste it into the platform.</span>
        <span>Each source keeps a history so you can regenerate or grab earlier versions.</span>
      </Bullets>
    </Section>
  </div>
);

const Transcripts = () => (
  <div className="space-y-8">
    <Lead>Every recording is transcribed automatically. The <UI>Transcripts</UI> tab in the Studio is your
      searchable library of every transcript — no need to dig into individual recordings to find what was said.</Lead>
    <Section title="Where to find it">
      <P>Studio → <UI>Transcripts</UI>. You’ll see every transcript, newest first, grouped by status.</P>
      <Bullets>
        <span><strong>Ready</strong> — finished transcripts you can read, search, copy and download.</span>
        <span><strong>In progress</strong> — recordings still transcribing; this list refreshes itself live.</span>
        <span><strong>Needs attention</strong> — anything that failed, so you can retry it from the recording.</span>
      </Bullets>
    </Section>
    <Section title="How transcripts are created">
      <P>Transcription is fully automatic. When a live show, Green Room session, or in-browser Studio recording
        finishes, it’s imported, transcribed, and then analyzed by AI — you don’t have to do anything.</P>
      <Steps>
        <Step title="Record or go live">Anything you capture in the Studio is saved to your Library.</Step>
        <Step title="Auto-transcribe">The audio is transcribed automatically (status moves Transcribing → Transcribed).</Step>
        <Step title="Auto-analyze">AI then pulls highlights and topics (Analyzing → Analyzed).</Step>
      </Steps>
      <Callout type="tip" title="Speaker labels">
        Transcripts are split by speaker with color-coded labels, so multi-guest conversations are easy to follow.
      </Callout>
    </Section>
    <Section title="Read, search & export">
      <Bullets>
        <span><strong>Search</strong> across every transcript by title — or by what was actually said.</span>
        <span><strong>Read in place</strong> — open any transcript in a clean reader with speaker labels.</span>
        <span><strong>Copy</strong> the full text, or <strong>Download</strong> it as a .txt file.</span>
        <span><strong>Open in Studio</strong> to jump to that recording for clips and social posts.</span>
      </Bullets>
    </Section>
    <Section title="Turn a transcript into content">
      <P>From a transcript you’re one step from shareable content — open it in the Studio to cut
        <Link className="mx-1 font-medium text-primary hover:underline" to="/portal/guides/recordings-clips">clips</Link>
        or generate
        <Link className="ml-1 font-medium text-primary hover:underline" to="/portal/guides/social-posts">social posts</Link>.</P>
    </Section>
  </div>
);

const ProducerSetup = () => (
  <div className="space-y-8">
    <Lead>
      You travel to a different venue every time, with the laptop as your production hub. This is the
      repeatable playbook: pack the kit, arrive, set up in ~15 minutes, and go live with confidence —
      whatever the room throws at you.
    </Lead>

    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-center gap-2">
        <Printer className="h-4 w-4 text-primary" />
        <p className="text-sm font-semibold text-foreground">Take the checklist with you</p>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        A one-page pre-show checklist you can print or save as a PDF and tick off at every venue.
      </p>
      <Button asChild size="sm" className="mt-3">
        <Link to="/portal/guides/checklist" data-testid="open-checklist-btn">
          <ListChecks className="mr-2 h-4 w-4" /> Open the pre-show checklist
        </Link>
      </Button>
    </div>

    <Section title="Your kit">
      <P>This is the gear you carry. Anything marked optional just adds flexibility — the core kit alone
        can run a great show.</P>
      <CardGrid>
        <InfoCard icon={Aperture} title="Production laptop">Runs everything. Keep it plugged in; never broadcast on battery alone.</InfoCard>
        <InfoCard icon={Camera} title="Owl 3 (Meeting Owl)">360° camera + room mic over a single USB cable. Ideal for in-room panels.</InfoCard>
        <InfoCard icon={Mic} title="2× USB microphones">Great for hosts at a desk. See the microphone section for using more than one.</InfoCard>
        <InfoCard icon={Plug} title="USB-C hub + cables">So the Owl, mics, and power all connect at once. Bring spare cables.</InfoCard>
        <InfoCard icon={Headphones} title="Headphones">Essential — they stop echo on the broadcast.</InfoCard>
        <InfoCard icon={Signal} title="Verizon 5G hotspot">Your internet backup when venue Wi-Fi is weak.</InfoCard>
        <InfoCard icon={Smartphone} title="Phone (optional)">Doubles as an extra camera — open the show's "Add a camera" link on it.</InfoCard>
        <InfoCard icon={Lightbulb} title="Clip light + tape (optional)">A small light and gaffer tape tame bad venue lighting and loose cables.</InfoCard>
      </CardGrid>
    </Section>

    <Section title="When you arrive — the 15-minute setup">
      <Steps>
        <Step title="Power first">Plug in the laptop and hub. Find a seat near an outlet.</Step>
        <Step title="Get online & test it">Join the venue Wi-Fi (or your 5G hotspot). Open the app and load the Studio to confirm it is responsive.</Step>
        <Step title="Place the camera">Owl in the middle of the table for a panel; a webcam/laptop at eye level for a host. Avoid windows directly behind people.</Step>
        <Step title="Place the mics">One mic per host within ~12 inches, or the Owl in the center for the whole room.</Step>
        <Step title="Sort the lighting">Light on faces, not behind them. A clip light or facing a window beats a dark room.</Step>
        <Step title="Connect everything, then open the studio">With gear plugged in, open the show in the Producer Console.</Step>
        <Step title="Allow camera & mic">Click <UI>Allow</UI> in the browser the first time at a new venue/computer.</Step>
        <Step title="Pick your devices & test">Choose your camera and mic in the setup screen, watch the preview, say a few words to check the level, and do a 20-second test recording.</Step>
      </Steps>
    </Section>

    <Section title="Choosing your camera">
      <P>In the live/green-room setup screen, your camera appears in a dropdown. Pick the right one for the room:</P>
      <CardGrid>
        <InfoCard icon={Aperture} title="Laptop camera">Fastest for a solo host. Raise the laptop to eye level.</InfoCard>
        <InfoCard icon={Camera} title="Owl 3">Plug in via USB and select <UI>Meeting Owl 3</UI> as the camera. Best for in-room groups — it auto-frames whoever is talking.</InfoCard>
        <InfoCard icon={Webcam} title="USB webcam">Select it from the camera dropdown. Great for a fixed, framed host shot.</InfoCard>
        <InfoCard icon={Smartphone} title="Phone as a camera">Two easy paths — see below.</InfoCard>
      </CardGrid>
      <Callout type="tip" title="Using a phone as a camera">
        The easiest way: have the person on the phone open your show's <strong>guest link</strong> — their
        phone camera joins like any guest, no extra apps. To use a phone as the host&apos;s webcam instead,
        install a webcam app (e.g. Camo or EpocCam) and connect by USB; the phone then shows up in the
        camera dropdown.
      </Callout>
    </Section>

    <Section title="Choosing your microphone">
      <P>Good audio matters more than video. Pick your mic from the dropdown in the setup screen.</P>
      <CardGrid>
        <InfoCard icon={Camera} title="Owl 3 room mic">Select the Owl as the mic to capture everyone around the table.</InfoCard>
        <InfoCard icon={Mic} title="A USB mic">Pick it from the dropdown for a crisp single-host sound.</InfoCard>
        <InfoCard icon={Mic} title="Lav mic">A USB lav (or a lav plugged into a USB interface/receiver) shows up as a normal mic — just select it.</InfoCard>
        <InfoCard icon={Smartphone} title="Phone guest">A remote/phone guest uses their own mic automatically when they join as a guest.</InfoCard>
      </CardGrid>
      <Callout type="warn" title="Important: a browser uses ONE microphone at a time">
        If you want two in-room USB mics on the same laptop, the browser can only listen to a single input
        device. To combine two mics you need a small <strong>USB audio interface or mixer</strong> that merges them into one input — then select that combined device in the console. For
        a panel, the simplest reliable option is the Owl for the room, with remote guests joining by link
        (each on their own mic).
      </Callout>
    </Section>

    <Section title="Internet & bandwidth">
      <P>Broadcasting is the most demanding thing you will do. Treat the connection as part of your kit.</P>
      <Steps>
        <Step title="Test before the guests arrive">Load the Studio and (if going live) do a short test broadcast.</Step>
        <Step title="Prefer the strongest link">A wired connection or a strong venue Wi-Fi is ideal. If Wi-Fi is weak or crowded, switch to your <strong>5G hotspot</strong>.</Step>
        <Step title="Reduce the load">Close other apps and downloads, and ask others not to hog the same Wi-Fi.</Step>
        <Step title="Lower the resolution if needed">If video looks choppy, drop to 720p — smooth 720p beats stuttery 1080p.</Step>
      </Steps>
      <Callout type="tip" title="Hotspot tip">
        Your unlimited Verizon 5G hotspot is a reliable plan B. Place it near a window for the best signal,
        and keep it charged/plugged in.
      </Callout>
    </Section>

    <Section title="Lighting & framing (quick wins)">
      <Bullets>
        <span>Put light <strong>in front of</strong> faces, never behind them.</span>
        <span>Camera at eye level — raise the laptop on a stand or a stack of books.</span>
        <span>Leave a little headroom; keep faces in the upper-middle of frame.</span>
        <span>Tidy the background; tape down loose cables so no one trips.</span>
      </Bullets>
    </Section>

    <Section title="Troubleshooting">
      <Bullets>
        <span><strong>No camera or mic?</strong> Check the browser permission (click <UI>Allow</UI>), make sure the device is selected in the dropdown, then re-plug the USB and refresh.</span>
        <span><strong>Echo / feedback?</strong> Everyone in the room should wear headphones; never run open speakers near a live mic.</span>
        <span><strong>A guest can&apos;t be heard?</strong> Make sure you admitted them, and ask them to allow their mic and pick the right input.</span>
        <span><strong>Owl not detected?</strong> Use a powered USB-C hub or a direct port, then refresh the setup screen so it re-scans devices.</span>
        <span><strong>Stream won&apos;t go live?</strong> Confirm you picked a destination and your connection is stable; switch to the 5G hotspot if Wi-Fi is flaky.</span>
        <span><strong>Choppy video?</strong> Lower to 720p, close other apps, or switch networks.</span>
      </Bullets>
    </Section>
  </div>
);

/* ------------------------------------------------------------------ */
/* LEADERSHIP / ADMIN GUIDES                                           */
/* ------------------------------------------------------------------ */

const NameCards = () => (
  <div className="space-y-8">
    <Lead>Two kinds of name tags: printed QR badges for the table, and on-screen name plates for the show.</Lead>
    <Section title="On-screen name plates (lower thirds)">
      <Steps>
        <Step title="Details come from the guest">When a guest joins by link they type their name, title and organization. Returning guests are filled in from the plate library. Hosts and cameras can be edited from their tile.</Step>
        <Step title="Plates show themselves">Whenever you switch someone on air, their plate slides in for a few seconds and slides out again (set the seconds or turn this off under <UI>Overlays → Name plate</UI>).</Step>
        <Step title="Tap the tag icon (or press N)">Tap the tag on any tile to flash that person's plate; shift-click (or shift+N) keeps it on until you hide it. <UI>N</UI> works on whoever is on air.</Step>
        <Step title="Pick a look">Four styles (Classic bar, Broadcast blocks, Pill, Minimal text), left / center / right, normal or large — all in the Name plate card. Or type any name/title/org manually and press <UI>Flash</UI> or <UI>Show</UI>.</Step>
      </Steps>
      <P>Name plates are drawn into the program output, so they appear identically in the live stream and the recording.
        Every plate you edit is saved to the <UI>Name plate library</UI> on the Studio home page.</P>
    </Section>
    <Section title="Printed QR name badges">
      <P>Library &amp; Tools → <UI>Name badges</UI> lets you pick members and print table badges with each person's
        QR code. Guests scan a badge to open the member's digital card.</P>
    </Section>
  </div>
);

const VoiceRoom = () => (
  <div className="space-y-8">
    <Lead>The Core Voice Room is a private, always-on audio room for the leadership team — like a
      drop-in voice channel. No scheduling, no recording.</Lead>
    <Section title="Join">
      <Steps>
        <Step title="Open Voice Room">Leadership → <UI>Voice Room</UI>.</Step>
        <Step title="Allow your mic & join">Click <UI>Join voice room</UI> and allow microphone access. It is audio-only and open mic.</Step>
      </Steps>
    </Section>
    <Section title="Good habits">
      <Bullets>
        <span>Mute yourself when you are not speaking in a busy room.</span>
        <span>Wear headphones to avoid echo.</span>
        <span>Leave the room when you are done to free it up.</span>
      </Bullets>
    </Section>
  </div>
);

const MembersRoles = () => (
  <div className="space-y-8">
    <Lead>Access in the portal is driven by roles. Here is who sees and does what.</Lead>
    <Section title="The roles">
      <Bullets>
        <span><strong>Member</strong> — everyone: Dashboard, Podcast Hub, Directory, Clips, Rewards, My Account.</span>
        <span><strong>Producer</strong> — unlocks the Studio (live shows, Green Room, recordings, clips, social).</span>
        <span><strong>Leadership / Core</strong> — adds Members, the Voice Room, and Analytics.</span>
        <span><strong>Admin / Owner</strong> — adds the Manage hub and the ability to assign roles.</span>
      </Bullets>
    </Section>
    <Section title="Managing members">
      <P>Open <UI>Members</UI> (Leadership) to review the directory, manage podcast guest requests, and —
        if you are an admin — adjust roles and permissions.</P>
    </Section>
    <Section title="How people join">
      <Bullets>
        <span><strong>Invited or via QR</strong> — anyone who signs up through a member's invite link, a member card, or a QR code is approved automatically and becomes a full member right away.</span>
        <span><strong>Found us on their own</strong> — sign-ups with no invitation arrive as <strong>pending</strong> and wait for an organizer to approve them.</span>
        <span><strong>Podcast eligibility</strong> — every member is podcast-eligible by default (they get the <strong>podcast</strong> badge). You can still revoke it for an individual from <UI>Members</UI>.</span>
      </Bullets>
    </Section>
  </div>
);

const Manage = () => (
  <div className="space-y-8">
    <Lead>The Manage hub is admin headquarters for everything operational. Open it from <UI>Manage</UI> in the sidebar.</Lead>
    <Section title="The tabs">
      <Bullets>
        <span><strong>Events</strong> — create and manage events and RSVPs.</span>
        <span><strong>Locations</strong> — the venues you run events from.</span>
        <span><strong>Media</strong> — images and media assets.</span>
        <span><strong>Storage</strong> — where large files (recordings, uploads) live.</span>
        <span><strong>QR Codes</strong> — generate QR codes for sign-ups and sharing.</span>
        <span><strong>Inquiries</strong> — incoming messages; the tab shows a count of new ones.</span>
      </Bullets>
    </Section>
  </div>
);

/* ------------------------------------------------------------------ */
/* REGISTRY                                                            */
/* ------------------------------------------------------------------ */

export const ARTICLES = {
  welcome: Welcome,
  "member-card": MemberCard,
  "podcast-hub": PodcastHubGuide,
  clips: Clips,
  rewards: Rewards,
  "studio-overview": StudioOverview,
  "going-live": GoingLive,
  "guests-cameras": GuestsCameras,
  "name-cards": NameCards,
  "recordings-clips": RecordingsClips,
  transcripts: Transcripts,
  "social-posts": SocialPosts,
  "producer-setup": ProducerSetup,
  "voice-room": VoiceRoom,
  "members-roles": MembersRoles,
  manage: Manage,
};
